import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { KeyRound, Copy, Check, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const ResetPasswordModal = ({ isOpen, onClose, user, userType = 'user' }) => {
  const [mode, setMode] = useState('choose');
  const [customPassword, setCustomPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReset = async (useCustom) => {
    if (useCustom && customPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.put(`/admin/reset-password/${user._id || user.userId}`, {
        new_password: useCustom ? customPassword : '',
        user_type: userType,
      });
      if (response.data.auto_generated) {
        setGeneratedPassword(response.data.new_password);
        setMode('result');
      } else {
        toast({ title: 'Success', description: 'Password has been reset successfully' });
        handleClose();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reset password', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setMode('choose');
    setCustomPassword('');
    setGeneratedPassword('');
    setCopied(false);
    onClose();
  };

  const userName = user?.name || user?.companyName || 'User';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="reset-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-pink-600" />
            Reset Password
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600 mb-4">
          Reset password for <span className="font-semibold text-gray-900">{userName}</span>
        </p>

        {mode === 'choose' && (
          <div className="space-y-3">
            <Button
              className="w-full justify-start gap-2 bg-pink-600 hover:bg-pink-700"
              onClick={() => setMode('custom')}
              data-testid="reset-password-custom-btn"
            >
              <KeyRound className="h-4 w-4" />
              Set Custom Password
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => handleReset(false)}
              disabled={loading}
              data-testid="reset-password-auto-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Generating...' : 'Auto-Generate Password'}
            </Button>
          </div>
        )}

        {mode === 'custom' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">New Password</label>
              <Input
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={customPassword}
                onChange={(e) => setCustomPassword(e.target.value)}
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode('choose')} className="flex-1">
                Back
              </Button>
              <Button
                className="flex-1 bg-pink-600 hover:bg-pink-700"
                onClick={() => handleReset(true)}
                disabled={loading || customPassword.length < 6}
                data-testid="reset-password-confirm-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        )}

        {mode === 'result' && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium mb-2">Password generated successfully!</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border rounded text-sm font-mono" data-testid="generated-password">
                  {generatedPassword}
                </code>
                <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-password-btn">
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-green-600 mt-2">Share this password with the user securely.</p>
            </div>
            <Button className="w-full" onClick={handleClose} data-testid="reset-password-done-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;
